'use client'

import { useState, Suspense } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Input } from '@/components/ui'
import { createClient } from '@/lib/supabase/client'
import posthog from 'posthog-js'

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  )
}

function LoginForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [linkSent, setLinkSent] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirect = searchParams.get('redirect') || '/dashboard'
  // /callback has redirected here with ?error=auth since it was written, and
  // this page has silently ignored it just as long — so a failed OAuth or
  // magic-link round trip looked like an ordinary login screen with no
  // explanation of why you were back at it.
  const authFailed = searchParams.get('error') === 'auth'

  /**
   * Magic link is the only way in, as of 2026-09-21 — except here.
   *
   * `?password=1` restores the password form and the OAuth row. It is not
   * advertised anywhere and no link points at it, so a member never meets it.
   * It exists for two reasons, and the first is not optional:
   *
   *   1. `e2e/fixtures/auth.setup.ts` signs in through the password form, and
   *      every authenticated Playwright project depends on it. A magic link
   *      cannot be automated — it needs an inbox — so removing the form outright
   *      would end UI sign-in for the 43 read-only browser tests permanently.
   *   2. Supabase caps auth emails per hour. Magic link being the ONLY door
   *      means hitting that cap locks everyone out, staff included. This is the
   *      way back in.
   *
   * ⚠️ None of the password or OAuth plumbing was deleted — `handleLogin` and
   * `handleOAuth` are untouched below, and Google/LinkedIn are expected back.
   * This is a change to what the page SHOWS, deliberately reversible by deleting
   * one condition.
   */
  const showPassword = searchParams.get('password') === '1'

  async function handleOAuth(provider: 'google' | 'linkedin_oidc') {
    const supabase = createClient()
    await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/callback`,
      },
    })
  }

  /**
   * Magic-link sign-in. Requests the email; the link itself lands on
   * `/magic-link`, which is where anything is actually verified — see that
   * page's header for why it is not `{{ .ConfirmationURL }}` and `/callback`.
   *
   * `shouldCreateUser: false` keeps signup on /signup, where consent and profile
   * creation live. GoTrue then answers an unknown address with "Signups not
   * allowed for otp", which is treated as success here **on purpose**: showing a
   * different screen for an address we have never seen turns this form into an
   * account-enumeration oracle.
   */
  async function handleMagicLink() {
    setError(null)

    if (!email) {
      setError('Enter your email address first.')
      return
    }

    setLoading(true)
    const supabase = createClient()
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    })

    // No `emailRedirectTo`: the template links to /magic-link directly, so the
    // redirect allow-list is not in the path at all.
    if (error && !/signups not allowed/i.test(error.message)) {
      setError(error.message)
      setLoading(false)
      return
    }

    posthog.capture('login_submitted', { method: 'magic_link' })
    setLinkSent(true)
    setLoading(false)
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const supabase = createClient()
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })

    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }

    if (data.user) {
      posthog.identify(data.user.id, { email: data.user.email })
    }
    posthog.capture('login_submitted', { method: 'email' })

    router.push(redirect)
  }

  // The same full-page state signup uses after `signUp`, and for the same
  // reason: the next thing that happens is in an inbox, not on this page.
  if (linkSent) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-md bg-white rounded-2xl p-10 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">&#10003;</span>
            </div>
            <h2 className="text-h2 font-bold text-text-primary mb-2">Check your email</h2>
            <p className="text-sm text-text-secondary mb-6">
              If <strong>{email}</strong> has a CRMPeeps account, a sign-in link is on its way. It
              lasts an hour and works once.
            </p>
            <button
              onClick={() => setLinkSent(false)}
              className="px-6 py-2 border border-border-default rounded-lg text-sm font-medium text-text-primary hover:bg-bg-subtle transition-colors"
            >
              Back to sign in
            </button>
          </div>
        </div>

        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Main content — centered card */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-white rounded-2xl p-10">
          {/* Heading */}
          <h1 className="text-h2 font-bold text-text-primary text-center mb-1">
            Welcome back
          </h1>
          <p className="text-sm text-text-secondary text-center mb-8">
            {showPassword ? 'Log in to continue' : "We'll email you a link. No password to remember."}
          </p>

          {authFailed && !error && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              That sign-in link didn&apos;t work — it may have expired or already been used. Try
              again below.
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-danger">
              {error}
            </div>
          )}

          {/* The email field is shared by both paths — magic link needs it, and
              so does the password form behind `?password=1`. */}
          <form onSubmit={showPassword ? handleLogin : (e) => { e.preventDefault(); handleMagicLink() }} className="space-y-4">
            <Input
              id="email"
              label="Email *"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            {showPassword && (
              <>
                <Input
                  id="password"
                  label="Password *"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />

                <div className="flex justify-end">
                  <Link href="/forgot-password" className="text-xs text-text-secondary hover:text-text-primary transition-colors">
                    Forgot password?
                  </Link>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-3 rounded-lg font-medium text-sm hover:bg-black/90 transition-colors disabled:opacity-50"
            >
              {showPassword
                ? loading ? 'Signing in...' : 'Continue'
                : loading ? 'Sending...' : 'Email me a sign-in link'}
            </button>
          </form>

          {/* Google and LinkedIn are expected back — `handleOAuth` is untouched.
              Hidden for now so there is one way in, which is what makes the
              screen worth reading. */}
          {showPassword && (
            <>
              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-border-default" />
                <span className="text-xs text-text-muted">or continue with</span>
                <div className="flex-1 h-px bg-border-default" />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => handleOAuth('google')}
                  className="flex-1 py-2.5 border border-border-default rounded-lg text-sm font-medium text-text-primary hover:bg-bg-subtle transition-colors"
                >
                  Google
                </button>
                <button
                  type="button"
                  onClick={() => handleOAuth('linkedin_oidc')}
                  className="flex-1 py-2.5 border border-border-default rounded-lg text-sm font-medium text-text-primary hover:bg-bg-subtle transition-colors"
                >
                  LinkedIn
                </button>
              </div>
            </>
          )}

          <p className="mt-6 text-center text-sm text-text-secondary">
            Don&apos;t have an account?{' '}
            <Link href="/signup" className="font-medium text-text-primary underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  )
}

/**
 * Shared by the sign-in card and the "check your email" state, so the two
 * cannot drift apart.
 */
function Footer() {
  return (
      <footer className="pb-8 pt-4 flex flex-col items-center gap-4">
        <div className="flex items-center gap-2">
          <Image
            src="/crmpeeps-logo-white.png"
            alt="CRMPeeps"
            width={140}
            height={32}
          />
          <span className="text-[10px] text-white/60 border-l border-white/20 pl-2 font-medium">
            beta
          </span>
        </div>
        <div className="flex items-center gap-5">
          {/* Instagram */}
          <a href="https://instagram.com/crmpeeps" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
          </a>
          {/* LinkedIn */}
          <a href="https://linkedin.com/company/crmpeeps" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          </a>
          {/* YouTube */}
          <a href="https://youtube.com/@crmpeeps" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
          </a>
        </div>
        <p className="text-xs text-white/40">&copy; 2026 CRMPeeps. All rights reserved.</p>
      </footer>
  )
}
